import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Star, Download, Trash2 } from "lucide-react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useApi, errMsg, eur, dateFr, ROLE_LABELS } from "@/lib/api";
import { PageShell, ListingCard, StatusPill, Empty, Photo, Pill } from "@/components/common";
import { CitySelect } from "@/components/DeliveryQuoteForm";
import { useMe } from "@/context/MeContext";

function useLoad(path) {
  const api = useApi();
  const [data, setData] = useState(null);
  const load = useCallback(() => api.get(path).then((r) => setData(r.data)), [api, path]);
  useEffect(() => { load(); }, [load]);
  return [data, load, api];
}

const run = async (fn, ok, after) => { try { await fn(); if (ok) toast.success(ok); after?.(); } catch (e) { toast.error(errMsg(e)); } };

function MyListings() {
  const [items] = useLoad("/me/listings");
  if (!items) return null;
  if (!items.length) return <Empty testid="my-listings-empty">Vous n'avez pas encore d'annonce. <Link to="/publier" className="underline">Publier</Link></Empty>;
  return <div className="grid grid-cols-2 md:grid-cols-4 gap-4" data-testid="my-listings-grid">{items.map((l) => (
    <div key={l.id} className="space-y-2"><ListingCard l={l} /><div className="flex gap-1 flex-wrap"><StatusPill status={l.status} /><StatusPill status={l.moderation_status} /></div></div>
  ))}</div>;
}

function Favorites() {
  const [items] = useLoad("/me/favorites");
  if (!items) return null;
  if (!items.length) return <Empty testid="favorites-empty">Aucun favori.</Empty>;
  return <div className="grid grid-cols-2 md:grid-cols-4 gap-4">{items.map((l) => <ListingCard key={l.id} l={l} />)}</div>;
}

function ResRow({ r, children }) {
  return (
    <div className="bg-white rounded-2xl border border-stone-200 p-4 flex flex-wrap items-center gap-3 justify-between" data-testid={`reservation-row-${r.id}`}>
      <div>
        <Link to={`/annonce/${r.listing_id}`} className="font-medium hover:underline">{r.listing_title}</Link>
        <p className="text-xs text-stone-500">{r.listing_type === "donation" ? "Don" : "Vente"} · {r.requester_name} · {dateFr(r.created_at)}{r.expires_at && ` · expire ${dateFr(r.expires_at)}`}</p>
        {r.message && <p className="text-sm text-stone-600 mt-1">« {r.message} »</p>}
      </div>
      <div className="flex items-center gap-2"><StatusPill status={r.status} testid={`reservation-status-${r.id}`} />{children}</div>
    </div>
  );
}

function Reservations() {
  const [data, load, api] = useLoad("/me/reservations");
  if (!data) return null;
  const post = (path, ok) => run(() => api.post(path), ok, load);
  return (
    <div className="grid lg:grid-cols-2 gap-8">
      <div className="space-y-3">
        <p className="eyebrow">Demandes reçues</p>
        {!data.received.length && <Empty testid="received-empty">Aucune demande reçue.</Empty>}
        {data.received.map((r) => (
          <ResRow key={r.id} r={r}>
            {r.status === "pending" && <>
              <Button size="sm" data-testid={`accept-reservation-${r.id}`} className="rounded-full bg-forest" onClick={() => post(`/reservations/${r.id}/accept`, "Réservation acceptée")}>Accepter</Button>
              <Button size="sm" variant="outline" data-testid={`reject-reservation-${r.id}`} className="rounded-full" onClick={() => post(`/reservations/${r.id}/reject`, "Demande refusée")}>Refuser</Button>
            </>}
            {["accepted", "paid"].includes(r.status) && r.listing_type === "donation" && <Button size="sm" data-testid={`handover-${r.id}`} className="rounded-full bg-terra" disabled={r.status !== "paid"} title={r.status !== "paid" ? "En attente du règlement des frais de don par le demandeur" : ""} onClick={() => post(`/reservations/${r.id}/handover`, "Remise confirmée")}>{r.status === "paid" ? "Confirmer la remise" : "Frais de don non réglés"}</Button>}
            {r.status === "accepted" && <Button size="sm" variant="outline" data-testid={`cancel-reservation-${r.id}`} className="rounded-full" onClick={() => post(`/reservations/${r.id}/cancel`, "Réservation annulée")}>Annuler</Button>}
          </ResRow>
        ))}
      </div>
      <div className="space-y-3">
        <p className="eyebrow">Mes demandes</p>
        {!data.sent.length && <Empty testid="sent-empty">Aucune demande envoyée.</Empty>}
        {data.sent.map((r) => (
          <ResRow key={r.id} r={r}>
            {r.status === "accepted" && <Link to={`/annonce/${r.listing_id}`}><Button size="sm" data-testid={`open-reservation-${r.id}`} className="rounded-full bg-forest">{r.listing_type === "sale" ? "Payer" : "Régler les frais"}</Button></Link>}
            {["pending", "accepted"].includes(r.status) && <Button size="sm" variant="outline" data-testid={`cancel-sent-${r.id}`} className="rounded-full" onClick={() => post(`/reservations/${r.id}/cancel`, "Annulée")}>Annuler</Button>}
          </ResRow>
        ))}
      </div>
    </div>
  );
}

function OrderCard({ o, buyer, reload, api }) {
  const [rating, setRating] = useState(5);
  const [comment, setComment] = useState("");
  const [dispute, setDispute] = useState("");
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-white rounded-2xl border border-stone-200 p-4 space-y-3" data-testid={`order-card-${o.id}`}>
      <div className="flex gap-3 items-center">
        <Photo src={o.listing_photo} alt="" className="w-14 h-14 rounded-xl" />
        <div className="flex-1">
          <p className="font-medium">{o.listing_title}</p>
          <p className="text-xs text-stone-500">{dateFr(o.created_at)} · {buyer ? `Total ${eur(o.buyer_total)}` : `Vente ${eur(o.item_price)}`}</p>
        </div>
        <StatusPill status={o.status} testid={`order-status-${o.id}`} />
      </div>
      <div className="flex flex-wrap gap-2">
        {buyer && o.status === "pending_payment" && <Link to={`/paiement/${o.listing_id}`}><Button size="sm" className="rounded-full bg-terra" data-testid={`resume-payment-${o.id}`}>Reprendre le paiement</Button></Link>}
        {o.delivery_quote_id && o.status !== "pending_payment" && <Link to={`/livraison`}><Button size="sm" variant="outline" className="rounded-full" data-testid={`order-delivery-${o.id}`}>Livraison</Button></Link>}
        {buyer && ["paid", "delivered"].includes(o.status) && <Button size="sm" data-testid={`confirm-receipt-${o.id}`} className="rounded-full bg-forest" onClick={() => run(() => api.post(`/orders/${o.id}/confirm-receipt`), "Réception confirmée, fonds libérés", reload)}>Confirmer la réception</Button>}
        {["paid", "delivered", "completed"].includes(o.status) && <Button size="sm" variant="outline" data-testid={`open-dispute-${o.id}`} className="rounded-full" onClick={() => setOpen(!open)}>Réclamation</Button>}
        {o.item_price > 0 && ["paid", "delivered"].includes(o.status) && <Pill tone="warn" testid={`escrow-held-${o.id}`}>{buyer ? "Paiement conservé jusqu'à votre validation" : "Fonds bloqués jusqu'à validation de l'acheteur"}</Pill>}
        {o.item_price > 0 && o.status === "completed" && <Pill tone="green" testid={`escrow-released-${o.id}`}>{buyer ? "Fonds versés au vendeur" : `Crédité sur votre porte-monnaie : ${eur(o.item_price)}`}</Pill>}
        {o.refund_due && <Pill tone="warn">Remboursement à traiter</Pill>}
        {o.disputed && <Pill tone="red">Litige ouvert</Pill>}
      </div>
      {open && <div className="flex gap-2"><Input data-testid={`dispute-input-${o.id}`} value={dispute} onChange={(e) => setDispute(e.target.value)} placeholder="Décrivez le problème" className="bg-white" /><Button size="sm" data-testid={`dispute-submit-${o.id}`} className="rounded-full bg-terra" onClick={() => run(() => api.post(`/orders/${o.id}/dispute`, { reason: dispute }), "Réclamation transmise", () => { setOpen(false); reload(); })}>Envoyer</Button></div>}
      {buyer && o.status === "completed" && !o.reviewed && (
        <div className="space-y-2 border-t border-stone-100 pt-3" data-testid={`review-form-${o.id}`}>
          <div className="flex gap-1">{[1, 2, 3, 4, 5].map((n) => <button key={n} data-testid={`review-star-${o.id}-${n}`} onClick={() => setRating(n)}><Star className={`w-5 h-5 ${n <= rating ? "fill-amber-400 text-amber-400" : "text-stone-300"}`} /></button>)}</div>
          <Textarea data-testid={`review-comment-${o.id}`} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Votre avis sur le vendeur" />
          <Button size="sm" data-testid={`review-submit-${o.id}`} className="rounded-full bg-forest" onClick={() => run(() => api.post("/reviews", { order_id: o.id, rating, comment }), "Avis publié", reload)}>Publier l'avis</Button>
        </div>
      )}
    </div>
  );
}

function Orders() {
  const [data, load, api] = useLoad("/me/orders");
  if (!data) return null;
  return (
    <div className="grid lg:grid-cols-2 gap-8">
      <div className="space-y-3"><p className="eyebrow">Achats</p>{!data.bought.length && <Empty testid="bought-empty">Aucun achat.</Empty>}{data.bought.map((o) => <OrderCard key={o.id} o={o} buyer reload={load} api={api} />)}</div>
      <div className="space-y-3"><p className="eyebrow">Ventes</p>{!data.sold.length && <Empty testid="sold-empty">Aucune vente.</Empty>}{data.sold.map((o) => <OrderCard key={o.id} o={o} reload={load} api={api} />)}</div>
    </div>
  );
}

function Profile() {
  const { me, refreshMe } = useMe();
  const api = useApi();
  const [f, setF] = useState(null);
  useEffect(() => {
    if (me) {
      setF({ display_name: me.display_name || "", city: me.city || "", bio: me.bio || "", business: { company_name: "", siret: "", activity: "", ...(me.business || {}) } });
    }
  }, [me]);
  if (!f) return null;
  const save = (extra = {}) => run(() => api.patch("/me", { ...f, city: f.city || null, ...extra }), "Profil enregistré", refreshMe);
  const setB = (k) => (e) => setF({ ...f, business: { ...f.business, [k]: e.target.value } });
  return (
    <div className="grid lg:grid-cols-2 gap-8">
      <div className="bg-white rounded-2xl border border-stone-200 p-6 space-y-4" data-testid="profile-form">
        <div className="flex gap-2 items-center"><Pill tone="forest" testid="profile-role">{ROLE_LABELS[me.role]}</Pill>{me.email_verified && <Pill tone="green">E-mail vérifié</Pill>}{me.suspended && <Pill tone="red" testid="profile-suspended">Compte suspendu</Pill>}</div>
        <p className="text-sm text-stone-500" data-testid="profile-email">{me.email}</p>
        <div><Label>Nom affiché</Label><Input data-testid="profile-name-input" value={f.display_name} onChange={(e) => setF({ ...f, display_name: e.target.value })} className="bg-white" /></div>
        <div><Label>Ville</Label><CitySelect testid="profile-city-select" value={f.city} onChange={(v) => setF({ ...f, city: v })} /></div>
        <div><Label>Présentation</Label><Textarea data-testid="profile-bio-input" value={f.bio} onChange={(e) => setF({ ...f, bio: e.target.value })} /></div>
        <Button data-testid="profile-save-btn" className="rounded-full bg-forest" onClick={() => save()}>Enregistrer</Button>
      </div>
      <div className="bg-white rounded-2xl border border-stone-200 p-6 space-y-4" data-testid="business-form">
        <p className="font-semibold text-forest">Professionnel ou prestataire de débarras</p>
        <p className="text-sm text-stone-500">Renseignez votre entreprise puis demandez un accès. Un administrateur valide chaque demande (agréments, assurances).</p>
        <div><Label>Raison sociale</Label><Input data-testid="business-name-input" value={f.business.company_name} onChange={setB("company_name")} className="bg-white" /></div>
        <div><Label>SIRET</Label><Input data-testid="business-siret-input" value={f.business.siret} onChange={setB("siret")} className="bg-white" /></div>
        <div><Label>Activité</Label><Input data-testid="business-activity-input" value={f.business.activity} onChange={setB("activity")} className="bg-white" /></div>
        {me.requested_role && <Pill tone="warn" testid="requested-role-pill">Demande en cours : {ROLE_LABELS[me.requested_role]}</Pill>}
        <div className="flex gap-2 flex-wrap">
          <Button data-testid="request-pro-btn" variant="outline" className="rounded-full" onClick={() => save({ requested_role: "pro" })}>Demander l'accès Pro</Button>
          <Button data-testid="request-provider-btn" variant="outline" className="rounded-full" onClick={() => save({ requested_role: "provider" })}>Devenir prestataire</Button>
        </div>
      </div>
    </div>
  );
}

function Privacy() {
  const api = useApi();
  const { me, refreshMe } = useMe();
  const exportData = async () => {
    const { data } = await api.get("/me/export");
    const url = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: "application/json" }));
    const a = document.createElement("a");
    a.href = url; a.download = "labradedie-mes-donnees.json"; a.click();
    URL.revokeObjectURL(url);
  };
  return (
    <div className="bg-white rounded-2xl border border-stone-200 p-6 space-y-4 max-w-2xl" data-testid="privacy-panel">
      <p className="text-sm text-stone-600">Conformément au RGPD, vous pouvez obtenir une copie de vos données ou demander leur suppression. Certaines données (transactions) peuvent être conservées pour obligations légales. <Link to="/legal/confidentialite" className="underline">Politique de confidentialité</Link></p>
      <div className="flex gap-3 flex-wrap">
        <Button data-testid="export-data-btn" variant="outline" className="rounded-full" onClick={exportData}><Download className="w-4 h-4 mr-2" />Exporter mes données</Button>
        <Button data-testid="delete-request-btn" variant="outline" className="rounded-full text-red-600" disabled={!!me?.deletion_requested_at} onClick={() => run(() => api.post("/me/delete-request"), "Demande de suppression enregistrée", refreshMe)}><Trash2 className="w-4 h-4 mr-2" />{me?.deletion_requested_at ? "Suppression demandée" : "Demander la suppression"}</Button>
      </div>
    </div>
  );
}

const WALLET_REASONS = { sale: "Vente encaissée", order_payment: "Achat payé", hold_released: "Paiement annulé (restitution)", refund: "Remboursement", refund_clawback: "Reprise après remboursement" };

function WalletTab() {
  const [w] = useLoad("/wallet");
  if (!w) return null;
  return (
    <div className="grid lg:grid-cols-12 gap-8" data-testid="wallet-panel">
      <div className="lg:col-span-5 rounded-2xl bg-forest text-white p-8">
        <p className="eyebrow !text-white/70">Solde disponible</p>
        <p className="font-serif text-5xl mt-3" data-testid="wallet-balance">{eur(w.balance)}</p>
        <p className="text-sm text-white/80 mt-4">L'argent de vos ventes arrive ici dès que l'acheteur valide la réception. Utilisez-le pour payer vos achats sur labradedie.fr (case « Utiliser mon porte-monnaie » au paiement).</p>
        <p className="text-xs text-white/60 mt-4">Virement vers votre compte bancaire : bientôt disponible.</p>
      </div>
      <div className="lg:col-span-7 space-y-2">
        <p className="eyebrow">Mouvements</p>
        {!w.transactions.length && <Empty testid="wallet-empty">Aucun mouvement pour l'instant.</Empty>}
        {w.transactions.map((t) => (
          <div key={t.id} className="bg-white rounded-xl border border-stone-200 p-3 flex justify-between text-sm" data-testid={`wallet-tx-${t.id}`}>
            <span>{WALLET_REASONS[t.reason] || t.reason} <span className="text-xs text-stone-500">· {dateFr(t.created_at)}</span></span>
            <b className={t.amount >= 0 ? "text-emerald-700" : "text-forest"}>{t.amount >= 0 ? "+" : ""}{eur(t.amount)}</b>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Account() {
  const [params, setParams] = useSearchParams();
  const tab = params.get("tab") || "listings";
  const tabs = [["listings", "Annonces", MyListings], ["favorites", "Favoris", Favorites], ["reservations", "Réservations", Reservations], ["orders", "Commandes", Orders], ["wallet", "Porte-monnaie", WalletTab], ["profile", "Profil & identité", Profile], ["privacy", "Confidentialité", Privacy]];
  return (
    <PageShell eyebrow="Mon espace" title="Mon compte" actions={<Link to="/messages"><Button variant="outline" className="rounded-full" data-testid="account-messages-btn">Messages</Button></Link>}>
      <Tabs value={tab} onValueChange={(v) => setParams({ tab: v })}>
        <TabsList className="bg-muted rounded-full p-1 h-auto flex-wrap justify-start mb-8">
          {tabs.map(([v, l]) => <TabsTrigger key={v} value={v} data-testid={`account-tab-${v}`} className="rounded-full data-[state=active]:bg-forest data-[state=active]:text-white px-4 py-2">{l}</TabsTrigger>)}
        </TabsList>
        {tabs.map(([v, , C]) => <TabsContent key={v} value={v}><C /></TabsContent>)}
      </Tabs>
    </PageShell>
  );
}
